
import { useEffect, useRef } from 'react';

export default function Hero() {
  const logoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (logoRef.current) {
        const scrollY = window.scrollY;
        logoRef.current.style.transform = `perspective(1500px) rotateY(${scrollY * 0.3}deg) rotateX(${scrollY * 0.15}deg) translateZ(${Math.sin(scrollY * 0.01) * 50}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 md:pt-0">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-purple-950 to-slate-950">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        {/* Watermark Pattern - AVNEX Brand Identity */}
        <div className="absolute inset-0 opacity-5 overflow-hidden">
          <div className="watermark-grid">
            {[...Array(20)].map((_, i) => (
              <img 
                key={i}
                src="https://static.readdy.ai/image/69020b62159dbcbb93ae23ea16bf9033/6fe36d98a62a7278966c853003907f00.png" 
                alt="" 
                className="watermark-logo"
                style={{ 
                  animationDelay: `${i * 0.2}s`,
                  transform: `rotate(${i % 2 === 0 ? -15 : 15}deg)`
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10 container mx-auto px-8 md:px-6 text-center mt-8 md:mt-0">
        {/* 3D Animated Logo with Advanced Effects */}
        <a 
          href="#services"
          className="cursor-pointer"
        >
          <div 
            ref={logoRef}
            className="mb-8 md:mb-12 inline-block transition-transform duration-300 ease-out logo-container"
            style={{ transformStyle: 'preserve-3d' }}
          >
            <div className="relative">
              {/* Glow Effect Rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="glow-ring glow-ring-1"></div>
                <div className="glow-ring glow-ring-2"></div>
                <div className="glow-ring glow-ring-3"></div>
              </div>
              
              {/* Main Logo */}
              <img 
                src="https://static.readdy.ai/image/69020b62159dbcbb93ae23ea16bf9033/6fe36d98a62a7278966c853003907f00.png" 
                alt="AVNEX Logo" 
                className="w-40 h-40 md:w-64 md:h-64 lg:w-80 lg:h-80 mx-auto drop-shadow-2xl animate-logo-float relative z-10"
              />
              
              {/* Particle Effects */}
              <div className="particles-container">
                {[...Array(12)].map((_, i) => (
                  <div 
                    key={i}
                    className="particle"
                    style={{
                      '--angle': `${i * 30}deg`,
                      animationDelay: `${i * 0.1}s`
                    } as React.CSSProperties}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </a>
        <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-white mb-6 tracking-tight px-4 md:px-0">
          Transformamos tu <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">Presencia Digital</span>
        </h1>
        
        <p className="text-base md:text-lg lg:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed px-8 md:px-4">
          Agencia de marketing digital especializada en diseño, redes sociales, publicidad y automatización para impulsar tu negocio al siguiente nivel
        </p>

        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center px-8 md:px-0">
          <a 
            href="#pricing" 
            className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Ver Planes
          </a>
          <a 
            href="#portfolio" 
            className="w-full sm:w-auto px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border-2 border-white/20 hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Ver Portafolio
          </a>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-4xl text-white/50"></i>
        </div>
      </div>

      <style>{`
        /* Logo Float Animation */
        @keyframes logo-float {
          0%, 100% { 
            transform: translateY(0px) scale(1);
          }
          25% {
            transform: translateY(-15px) scale(1.05) rotateZ(2deg);
          }
          50% { 
            transform: translateY(-25px) scale(1.08);
          }
          75% {
            transform: translateY(-15px) scale(1.05) rotateZ(-2deg);
          }
        }
        
        .animate-logo-float {
          animation: logo-float 4s ease-in-out infinite;
        }

        /* Glow Rings */
        .glow-ring {
          position: absolute;
          border-radius: 50%;
          border: 2px solid rgba(147, 51, 234, 0.3);
        }

        @keyframes ring-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.1;
          }
        }

        .glow-ring-1 {
          width: 200px;
          height: 200px;
          animation: ring-pulse 3s ease-in-out infinite;
        }

        .glow-ring-2 {
          width: 240px;
          height: 240px;
          border-color: rgba(59, 130, 246, 0.3);
          animation: ring-pulse 3s ease-in-out infinite 0.5s;
        }

        .glow-ring-3 {
          width: 280px;
          height: 280px;
          border-color: rgba(168, 85, 247, 0.3);
          animation: ring-pulse 3s ease-in-out infinite 1s;
        }

        @media (min-width: 768px) {
          .glow-ring-1 {
            width: 280px;
            height: 280px;
          }
          .glow-ring-2 {
            width: 320px;
            height: 320px;
          }
          .glow-ring-3 {
            width: 360px;
            height: 360px;
          }
        }

        @media (min-width: 1024px) {
          .glow-ring-1 {
            width: 320px;
            height: 320px;
          }
          .glow-ring-2 {
            width: 360px;
            height: 360px;
          }
          .glow-ring-3 {
            width: 400px;
            height: 400px;
          }
        }

        /* Particles */
        .particles-container {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .particle {
          position: absolute;
          width: 6px;
          height: 6px;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          border-radius: 50%;
          top: 50%;
          left: 50%;
          animation: particle-orbit 8s linear infinite;
          box-shadow: 0 0 10px rgba(147, 51, 234, 0.8);
        }

        @media (min-width: 768px) {
          .particle {
            width: 8px;
            height: 8px;
          }
        }

        @keyframes particle-orbit {
          0% {
            transform: rotate(var(--angle)) translateX(120px) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
            transform: rotate(var(--angle)) translateX(120px) scale(1);
          }
          90% {
            opacity: 1;
            transform: rotate(calc(var(--angle) + 360deg)) translateX(120px) scale(1);
          }
          100% {
            transform: rotate(calc(var(--angle) + 360deg)) translateX(120px) scale(0);
            opacity: 0;
          }
        }

        @media (min-width: 768px) {
          @keyframes particle-orbit {
            0% {
              transform: rotate(var(--angle)) translateX(150px) scale(0);
              opacity: 0;
            }
            10% {
              opacity: 1;
              transform: rotate(var(--angle)) translateX(150px) scale(1);
            }
            90% {
              opacity: 1;
              transform: rotate(calc(var(--angle) + 360deg)) translateX(150px) scale(1);
            }
            100% {
              transform: rotate(calc(var(--angle) + 360deg)) translateX(150px) scale(0);
              opacity: 0;
            }
          }
        }

        @media (min-width: 1024px) {
          @keyframes particle-orbit {
            0% {
              transform: rotate(var(--angle)) translateX(180px) scale(0);
              opacity: 0;
            }
            10% {
              opacity: 1;
              transform: rotate(var(--angle)) translateX(180px) scale(1);
            }
            90% {
              opacity: 1;
              transform: rotate(calc(var(--angle) + 360deg)) translateX(180px) scale(1);
            }
            100% {
              transform: rotate(calc(var(--angle) + 360deg)) translateX(180px) scale(0);
              opacity: 0;
            }
          }
        }

        /* Watermark Grid */
        .watermark-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 80px;
          padding: 40px;
          transform: rotate(-15deg) scale(1.5);
        }

        .watermark-logo {
          width: 120px;
          height: 120px;
          opacity: 0.4;
          animation: watermark-fade 8s ease-in-out infinite;
        }

        @keyframes watermark-fade {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
        }

        /* Logo Container Hover Effect */
        .logo-container:hover img {
          filter: drop-shadow(0 0 30px rgba(147, 51, 234, 0.8)) drop-shadow(0 0 60px rgba(59, 130, 246, 0.6));
        }
      `}</style>
    </section>
  );
}
